<template>
    <OssDataTable
        :items="userActivitiesHistory"
        :headers="dataHeader"
        fixed-header
        :fixed-footer="true"
        item-key="index"
        :display-row-number="true"
        :loading="loading"
        class="v-data-table--striped v-data-table--dense"
    >
        <template v-slot:[`item.date`]="{ item }">
            {{ formatTime(item.date) }}
        </template>
        <template v-slot:[`item.action`]="{ item }">
            {{ getActionTitle(item) }}
        </template>
        <template v-slot:[`item.company`]="{ item }">
            {{ fctName(item) }}
        </template>
        <template v-slot:[`item.user`]="{ item }">
            {{ getUserDisplayName(item.user) }}
        </template>
        <template v-slot:[`item.userBT`]="{ item }">
            {{ getUserDisplayName(item.user) }}
        </template>
        <!-- <template v-slot:item.email="{ item }">
            {{ item.user.email }}
        </template> -->
        <template v-slot:[`item.accountType`]="{ item }">
            {{ getAccountType(item.account.type) }}
        </template>
        <template v-slot:[`item.import`]="{ item }">
            {{ fctImport(item) }}
        </template>
    </OssDataTable>
</template>

<script lang="ts">
import moment from "moment-timezone";
import { Apollo } from "@/decorators";
import { getUserDisplayName } from "../services/userServices";

import USERS_ACTIVITIES_HISTORY from "../graphql/usersActivities/UserActivitiesHistory.gql";
import BANK_TRANSACTION_ACTIVITY from "../graphql/BankTransactionActivity.gql";
import DOCUMENT_ACTIVITY from "../graphql/usersActivities/DocumentActivity.gql";
import EXPENSE_ACTIVITY from "../graphql/usersActivities/ExpenseReportActivity.gql";
import ZTILL_ACTIVITY from "../graphql/usersActivities/ZTillReceiptActivity.gql";
import DECLARATION_ACTIVITY from "../graphql/usersActivities/DeclarationActivity.gql";

import OssDataTable from "./ossComponents/ossDataTable2.vue";
import { cloneDeep } from "@apollo/client/utilities";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";

@Component({
    components: {
        OssDataTable,
    },
})
export class UsersActivitiesHistory extends Vue {
    @Prop() objectId: string;
    @Prop() documentId: string;
    @Prop() expenseReportId: string;
    @Prop() zTillReceiptId: string;
    @Prop() bankTransactionId: string;
    @Prop() auditCheckKey: string;
    @Prop() accountingPeriodId: string;
    @Prop() declarationId;

    dataUser = [
        { title: "Date", value: "date", sortable: true },
        { title: "Action", value: "action" },
        { title: "Utilisateur", value: "user" },
        { title: "Société", value: "company" },
        { title: "Import", value: "import" }, // Origine
    ];

    dataDoc = [
        { title: "Date", value: "date", sortable: true },
        { title: "Action", value: "action" },
        { title: "Société", value: "company" },
    ];

    dataBank = [
        { title: "Date", value: "date", sortable: true },
        { title: "Action", value: "action" },
        { title: "Nom du compte", value: "company" },
        { title: "Type de compte", value: "accountType" },
        { title: "Utilisateur", value: "userBT" },
    ];

    get dataHeader() {
        if (this.userActivitiesHistory.length !== 0) {
            return this.dataUser;
        }
        if (this.bankTransactionId) {
            return this.dataBank;
        }
        if (this.userActivitiesHistory.length === 0) {
            return this.dataDoc;
        }
        return null;
    }

    getAccountType(type) {
        return this.$t("accountingItem.type." + type);
    }

    getUserDisplayName(user) {
        if (user) {
            return getUserDisplayName(user);
        }
        return null;
    }

    formatTime(date: moment.MomentInput) {
        if (!date) {
            return "";
        }
        const mDate = moment(date);
        if (!mDate.isValid()) {
            return date;
        }
        return mDate.format("DD/MM/YYYY, HH:mm");
    }

    timeDiff(dateOne: moment.MomentInput, dateTwo: moment.MomentInput) {
        return moment(dateOne).diff(moment(dateTwo), "seconds");
    }

    get object() {
        if (this.expenseActivity) {
            return this.expenseActivity;
        }
        if (this.zTillActivity) {
            return this.zTillActivity;
        }
        if (this.documentActivity) {
            return this.documentActivity;
        }
        if (this.bankTransactionActivity) {
            return this.bankTransactionActivity;
        }
        if (this.declarationActivity) {
            return this.declarationActivity;
        }
        return null;
    }

    get id() {
        if (this.objectId) {
            return this.objectId;
        }
        if (this.documentId) {
            return this.documentId;
        }
        if (this.expenseReportId) {
            return this.expenseReportId;
        }
        if (this.zTillReceiptId) {
            return this.zTillReceiptId;
        }
        if (this.bankTransactionId) {
            return this.bankTransactionId;
        }
        if (this.declarationId) {
            return this.declarationId;
        }
        return undefined;
    }

    // -------------------
    //   user activities
    // -------------------
    get loading() {
        if (this.$apollo.queries.dbUserActivitiesHistory) {
            return this.$apollo.queries.dbUserActivitiesHistory.loading;
        }
        return false;
    }

    @Apollo({
        query: USERS_ACTIVITIES_HISTORY,
        variables() {
            return {
                id: this.id,
                auditCheckKey: this.auditCheckKey,
                accountingPeriodId: this.accountingPeriodId,
            };
        },
        skip() {
            return !this.id && (!this.accountingPeriodId || !this.auditCheckKey);
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data?.userActivitiesHistory?.output) {
                this.dbUserActivitiesHistory = data?.userActivitiesHistory.output;
            } else {
                this.dbUserActivitiesHistory = [];
            }
        },
    })
    dbUserActivitiesHistory: any[] = [];

    get userActivitiesHistory() {
        const userActivitiesHistory = cloneDeep(this.dbUserActivitiesHistory);

        if (this.object) {
            // if (!userActivitiesHistory.length && this.bankTransactionId) {
            //     userActivitiesHistory = this.bankList(this.bankTransactionActivity)
            // }
            if (userActivitiesHistory.length) {
                const time = 60;

                let firstActivity = userActivitiesHistory[0];
                // const lastActivity = userActivitiesHistory[userActivitiesHistory.length - 1]
                if (firstActivity.action !== "CREATE") {
                    firstActivity = {
                        action: "CREATE",
                        date: this.object.createdAt,
                        object: this.object,
                    };
                    userActivitiesHistory.splice(0, 0, firstActivity);
                }

                if (this.timeDiff(this.object.updatedAt, this.object.createdAt) > time) {
                    let beforeUA;
                    let afterUA;
                    let index = 0;
                    for (const userActivity of userActivitiesHistory) {
                        const diff = this.timeDiff(this.object.updatedAt, userActivity.date);
                        if (diff > 0) {
                            beforeUA = userActivity;
                        } else if (diff < 0) {
                            afterUA = userActivity;
                            break;
                        }
                        index++;
                    }
                    if (!afterUA) {
                        userActivitiesHistory.push({
                            action: "UPDATE",
                            date: this.object.updatedAt,
                        });
                    } else {
                        userActivitiesHistory.splice(index, 0, {
                            action: "UPDATE",
                            date: this.object.updatedAt,
                        });
                    }
                    // userActivitiesHistory.push({
                    //     action: "UPDATE",
                    //     date: this.object.updatedAt
                    // })
                }
            } else {
                userActivitiesHistory.push({
                    action: "CREATE",
                    date: this.object.createdAt,
                    object: this.object,
                });
                if (this.object.createdAt < this.object.updatedAt) {
                    userActivitiesHistory.push({
                        action: "UPDATE",
                        date: this.object.updatedAt,
                    });
                }
            }
        }
        return userActivitiesHistory;
    }

    // -------------------
    //      DOCUMENT
    // -------------------
    @Apollo({
        query: DOCUMENT_ACTIVITY,
        variables() {
            return { documentID: this.documentId };
        },
        skip() {
            return !this.documentId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data) {
                this.documentActivity = data.document;
            } else {
                this.documentActivity = null;
            }
        },
    })
    documentActivity: any = null;

    @Apollo({
        query: EXPENSE_ACTIVITY,
        variables() {
            return { expenseReportID: this.expenseReportId };
        },
        skip() {
            return !this.expenseReportId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data) {
                this.expenseActivity = data.expenseReport;
            } else {
                this.expenseActivity = null;
            }
        },
    })
    expenseActivity: any = null;

    @Apollo({
        query: ZTILL_ACTIVITY,
        variables() {
            return { zTillReceiptID: this.zTillReceiptId };
        },
        skip() {
            return !this.zTillReceiptId;
        },
        result({ data, loading, networkStatus }: any) {
            if (data) {
                this.zTillActivity = data.zTillReceipt;
            } else {
                this.zTillActivity = null;
            }
        },
    })
    zTillActivity: any = null;

    // -------------------
    //      DECLARATION
    // -------------------
    @Apollo({
        query: DECLARATION_ACTIVITY,
        variables() {
            return { id: this.declarationId };
        },
        skip() {
            return !this.declarationId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data?.declaration) {
                this.declarationActivity = data.declaration;
            } else {
                this.declarationActivity = null;
            }
        },
    })
    declarationActivity: any = null;

    // fctAction(act) {
    //     let action
    //     if (act.action && !this.bankTransactionId) {
    //         action = this.$t(`userActions.action.${ act.action }`)
    //     } else if (this.bankTransactionId) {
    //         action = act.action
    //     } else {
    //         action = "Création"
    //     }
    //     return action
    // }

    fctName(act) {
        if (act?.user?.company) {
            return act.user.company.name;
        }
        if (this.bankTransactionActivity) {
            return this.bankTransactionActivity.account?.name || "";
        }
        return "bobbee";
    }

    fctImport(act) {
        if (!this.object) {
            return "";
        }
        if (this.object.fromDocument) {
            return "fromDocument " + this.object.fromDocument.importId.split("_")[0];
        }
        if (this.object.accountingItemImport) {
            return this.object.accountingItemImport.name;
        }
        if (this.object.importId) {
            return this.object.importId.split("_")[0];
        }
        return "";
    }

    @Apollo({
        query: BANK_TRANSACTION_ACTIVITY,
        variables() {
            return {
                bankTransactionID: this.bankTransactionId,
            };
        },
        skip() {
            return !this.bankTransactionId;
        },
        result({ data, loading, networkStatus }: any) {
            if (data) {
                this.bankTransactionActivity = JSON.parse(JSON.stringify(data.bankTransaction));
            } else {
                this.bankTransactionActivity = [];
            }
        },
    })
    bankTransactionActivity: any = [];

    bankList(act) {
        const list = [];
        const createdAtSet = new Set();
        if (act.importDocument) {
            if (act?.importDocument?.createdAt) {
                createdAtSet.add(moment(act.importDocument.createdAt).format("YYYY MM DD h"));
                list.push({
                    createdAt: act.importDocument.createdAt,
                    account: act.account,
                    actionTitle:
                        "Importation - " +
                        (act.importDocument.name ||
                            this.$t("document.type." + act.importDocument.type)),
                    user: act.importDocument.user,
                });
                if (this.timeDiff(act.createdAt, act.updatedAt) > 3600) {
                    list.push({
                        updatedAt: act.importDocument.updatedAt,
                        account: act.account,
                        action: "Modification Document",
                    });
                }
            }
        }
        if (act.accountingItemImport) {
            if (
                !createdAtSet.has(moment(act.accountingItemImport.createdAt).format("YYYY MM DD h"))
            ) {
                createdAtSet.add(moment(act.accountingItemImport.createdAt).format("YYYY MM DD h"));
                list.push({
                    createdAt: act.accountingItemImport.createdAt,
                    account: act.account,
                    actionTitle: "Import - " + act.accountingItemImport.type,
                });
                if (this.timeDiff(act.createdAt, act.updatedAt) > 3600) {
                    list.push({
                        updatedAt: act.accountingItemImport.updatedAt,
                        account: act.account,
                        action: "UPDATE",
                    });
                }
            }
        }
        if (act.accountingItemImport === null) {
            if (!createdAtSet.has(moment(act.createdAt).format("YYYY MM DD h"))) {
                createdAtSet.add(moment(act.createdAt).format("YYYY MM DD h"));
                if (act.importId) {
                    list.push({
                        createdAt: act.createdAt,
                        account: act.account,
                        actionTitle: "Import - " + act.importId.split("_")[0],
                    });
                }
            }
        }
        if (act.budgetInsightId) {
            if (!createdAtSet.has(moment(act.createdAt).format("YYYY-MM-DD-H"))) {
                createdAtSet.add(moment(act.createdAt).format("YYYY-MM-DD-H"));
                list.push({
                    createdAt: act.createdAt,
                    account: act.account,
                    actionTitle: "Importation - Powens (budgetInsight)",
                });
            }
        }
        if (act.createdAt) {
            if (!createdAtSet.has(moment(act.createdAt).format("YYYY MM DD h"))) {
                createdAtSet.add(moment(act.createdAt).format("YYYY MM DD h"));
                list.push({
                    createdAt: act.createdAt,
                    account: act.account,
                    action: "CREATE",
                });
                if (this.timeDiff(act.createdAt, act.updatedAt) > 3600) {
                    list.push({
                        updatedAt: act.updatedAt,
                        account: act.account,
                        action: "UPDATE",
                    });
                }
            }
        }
        if (act.accountingLines) {
            for (let i = 0; i < act.accountingLines.length; i++) {
                if (
                    !createdAtSet.has(
                        moment(act.accountingLines[i].createdAt).format("YYYY MM DD h"),
                    )
                ) {
                    createdAtSet.add(
                        moment(act.accountingLines[i].createdAt).format("YYYY MM DD h"),
                    );
                    list.push({
                        createdAt: act.accountingLines[i].createdAt,
                        account: act.account,
                        actionTitle: "Affectation ou lettrage",
                    });
                    if (this.timeDiff(act.createdAt, act.updatedAt) > 3600) {
                        list.push({
                            updatedAt: act.accountingLines[i].updatedAt,
                            account: act.account,
                            action: "UPDATE",
                        });
                    }
                }
            }
        }
        return list;
    }

    getActionTitle(item) {
        if (item.actionTitle) {
            return item.actionTitle;
        }
        if (item.actionObject) {
            return this.$t(
                `userActions.action.${item.actionDetail || item.action}_${item.actionObject.type}`,
            );
        }
        return this.$t(`userActions.action.${item.actionDetail || item.action}`);
    }
}

export default toNative(UsersActivitiesHistory);
</script>
